// Use environment variable if available, otherwise fallback to dev URL.
// In production this is same-origin via nginx → /api → backend.
const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin + '/api' || 'http://127.0.0.1:8000';

class ApiService {
  /** Generic fetch wrapper. credentials: 'include' so the JWT cookie is sent. */
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // 401 from middleware means session expired — surface a typed error
      // so the UI can redirect to /login without parsing message strings.
      if (response.status === 401) {
        const err = new Error('Not authenticated');
        err.status = 401;
        throw err;
      }
      if (!response.ok) {
        let detail;
        try {
          const body = await response.json();
          detail = body.detail;
          // FastAPI 422 validation errors are arrays of objects — flatten to a string.
          if (Array.isArray(detail)) {
            detail = detail
              .map((d) => {
                const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
                return `${loc}: ${d.msg || JSON.stringify(d)}`;
              })
              .join('; ');
          } else if (detail && typeof detail === 'object') {
            detail = JSON.stringify(detail);
          }
        } catch (_) { /* response wasn't JSON */ }
        throw new Error(detail || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // ---------------- Auth ----------------
  async authCheck() {
    return this.request('/auth/check');
  }
  async login(username, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }
  async logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }
  // Sign-up takes an email only; the password is chosen at the emailed link.
  async register(email) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
  // What an emailed /set-password link is for: {valid, purpose, email}.
  async linkInfo(token) {
    return this.request(`/auth/link?token=${encodeURIComponent(token)}`);
  }
  async setPassword(token, password) {
    return this.request('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }
  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  // ---------------- Individuals (was: patients) ----------------
  async getIndividuals() {
    return this.request('/individuals');
  }
  async getIndividual(individualId) {
    return this.request(`/individuals/${individualId}`);
  }
  async getIndividualsStats() {
    return this.request('/individuals/stats');
  }
  async createIndividual(individualData) {
    return this.request('/individuals', {
      method: 'POST',
      body: JSON.stringify(individualData),
    });
  }
  async updateIndividual(individualId, data) {
    return this.request(`/individuals/${individualId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ---------------- Care plan sections ----------------
  async getIndividualCarePlan(individualId) {
    return this.request(`/individuals/${individualId}/care-plan`);
  }
  async getCarePlanSection(individualId, sectionId, subsectionId = null) {
    let endpoint = `/individuals/${individualId}/care-plan/${sectionId}`;
    if (subsectionId) endpoint += `?subsection_id=${subsectionId}`;
    return this.request(endpoint);
  }
  async updateCarePlanSection(
    individualId,
    sectionId,
    content,
    subsectionId = null,
    sectionTitle = '',
    subsectionTitle = '',
  ) {
    let endpoint = `/individuals/${individualId}/care-plan/${sectionId}`;
    const params = new URLSearchParams();
    if (subsectionId) params.append('subsection_id', subsectionId);
    if (sectionTitle) params.append('section_title', sectionTitle);
    if (subsectionTitle) params.append('subsection_title', subsectionTitle);
    if (params.toString()) endpoint += `?${params.toString()}`;
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }
  async deleteCarePlanSection(individualId, sectionId, subsectionId = null) {
    let endpoint = `/individuals/${individualId}/care-plan/${sectionId}`;
    if (subsectionId) endpoint += `?subsection_id=${subsectionId}`;
    return this.request(endpoint, { method: 'DELETE' });
  }
  async batchUpdateCarePlanSections(individualId, sections, skipCompletionUpdate = false) {
    return this.request(
      `/individuals/${individualId}/care-plan/batch-update?skip_completion_update=${skipCompletionUpdate}`,
      { method: 'POST', body: JSON.stringify({ sections }) },
    );
  }

  // ---------------- Document import ----------------
  async uploadDocumentSimple(individualName, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(
      `${API_BASE_URL}/import/upload-simple/${encodeURIComponent(individualName)}`,
      { method: 'POST', credentials: 'include', body: formData },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Simple upload failed');
    }
    return response.json();
  }
  async getImportStatus(processId) {
    return this.request(`/import/status/${processId}`);
  }
  async listImportProcesses() {
    return this.request('/import/processes');
  }

  // ---------------- Admin ----------------
  async deleteIndividual(individualId) {
    // Allowed for admins and users granted "can delete records".
    return this.request(`/individuals/${individualId}`, { method: 'DELETE' });
  }
  async adminSetCanDelete(userId, canDelete) {
    return this.request(`/admin/users/${userId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ can_delete_records: canDelete }),
    });
  }
  async adminDashboard() {
    return this.request('/admin/dashboard');
  }
  async adminActivity(limit = 100, offset = 0) {
    return this.request(`/admin/activity?limit=${limit}&offset=${offset}`);
  }
  async adminUsage() {
    return this.request('/admin/usage');
  }
  async adminListUsers() {
    return this.request('/admin/users');
  }
  async adminApproveUser(userId) {
    return this.request(`/admin/users/${userId}/approve`, { method: 'POST' });
  }
  async adminRejectUser(userId) {
    return this.request(`/admin/users/${userId}/reject`, { method: 'POST' });
  }
  async adminDeleteUser(userId) {
    return this.request(`/admin/users/${userId}`, { method: 'DELETE' });
  }
  // Invites an address: it is emailed a link to choose a password.
  async adminInviteEmail(email) {
    return this.request('/admin/users/whitelist', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
  async adminListDomains() {
    return this.request('/admin/domains');
  }
  async adminAddDomain(domain) {
    return this.request('/admin/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }
  async adminRemoveDomain(domainId) {
    return this.request(`/admin/domains/${domainId}`, { method: 'DELETE' });
  }
  async adminRecordHistory(limit = 100, individualId = null) {
    const q = individualId != null
      ? `?limit=${limit}&individual_id=${individualId}`
      : `?limit=${limit}`;
    return this.request(`/admin/record-history${q}`);
  }

  // ---------------- AI Assist ----------------
  // options.plainText: for fields that display as plain text (no markdown).
  async aiAssist(sectionTitle, currentContent, guidelines, options = {}) {
    return this.request('/ai-assist', {
      method: 'POST',
      body: JSON.stringify({
        section_title: sectionTitle,
        current_content: currentContent,
        guidelines,
        plain_text: Boolean(options.plainText),
      }),
    });
  }

  // ---------------- Profile photos ----------------
  async uploadProfilePhoto(individualId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(
      `${API_BASE_URL}/individuals/${individualId}/profile-photo`,
      { method: 'POST', credentials: 'include', body: formData },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Photo upload failed');
    }
    return response.json();
  }
  getProfilePhotoUrl(individualId) {
    return `${API_BASE_URL}/individuals/${individualId}/profile-photo?t=${Date.now()}`;
  }
  async deleteProfilePhoto(individualId) {
    return this.request(`/individuals/${individualId}/profile-photo`, { method: 'DELETE' });
  }

  // ---------------- Receipt management (restored optional demo feature) ----------------
  async getReceiptStats() {
    return this.request('/receipts/stats');
  }
  async getReceipts(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/receipts${q ? '?' + q : ''}`);
  }
  async getReceipt(id) {
    return this.request(`/receipts/${id}`);
  }
  async approveReceipt(id, approvedBy = 'Admin') {
    return this.request(`/receipts/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: approvedBy }),
    });
  }
  async rejectReceipt(id, rejectedBy = 'Admin', reason = '') {
    return this.request(`/receipts/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejected_by: rejectedBy, reason: reason }),
    });
  }
  async updateReceipt(id, data) {
    return this.request(`/receipts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deleteReceipt(id) {
    return this.request(`/receipts/${id}`, { method: 'DELETE' });
  }
  async getCaretakers() {
    return this.request('/caretakers');
  }
  async createCaretaker(data) {
    return this.request('/caretakers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateCaretaker(id, data) {
    return this.request(`/caretakers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deleteCaretaker(id) {
    return this.request(`/caretakers/${id}`, { method: 'DELETE' });
  }
  getReceiptImageUrl(id) {
    return `${API_BASE_URL}/receipts/${id}/image?t=${Date.now()}`;
  }

  // ---------------- Backward-compat shims ----------------
  // Old callers used getPatient/getPatientCarePlan/etc. Keep these as thin
  // aliases so PatientCarePlan.js doesn't have to be edited line-by-line for
  // the rename. These will be removed in a future cleanup pass.
  async getPatients() { return this.getIndividuals(); }
  async getPatient(id) { return this.getIndividual(id); }
  async getPatientStats() { return this.getIndividualsStats(); }
  async getPatientCarePlan(id) { return this.getIndividualCarePlan(id); }
  async createPatient(data) { return this.createIndividual(data); }
  async deletePatient(id) { return this.deleteIndividual(id); }
}

export const apiService = new ApiService();
